import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface SupplierCategory {
  id: string;
  name: string;
  category: boolean;
  sub_category: boolean;
  category_number?: number;
}

export const useSupplierCategories = () => {
  return useQuery({
    queryKey: ['supplier-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('supplier_categories')
        .select('id, name, category, sub_category')
        .eq('active', true)
        .order('name');

      if (error) {
        console.error('Error fetching supplier categories:', error);
        throw error;
      }

      return data as SupplierCategory[];
    },
  });
};

export const useGroupedSupplierCategories = () => {
  return useQuery({
    queryKey: ['grouped-supplier-categories'],
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('supplier_categories')
        .select('id, name, category, sub_category, category_number')
        .eq('active', true)
        .order('category_number', { ascending: true });

      if (error) {
        console.error('Error fetching grouped supplier categories:', error);
        throw error;
      }

      const categories = data as SupplierCategory[];
      
      // Group categories with their subcategories
      const mainCategories = categories.filter(cat => cat.category);
      const grouped = mainCategories.map(mainCat => ({
        mainCategory: mainCat,
        subCategories: categories.filter(cat => 
          cat.sub_category && 
          !cat.category && 
          cat.category_number === mainCat.category_number
        )
      }));

      return grouped;
    },
  });
};

export const useGroupedMatchableSupplierCategories = () => {
  return useQuery({
    queryKey: ['grouped-matchable-supplier-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('supplier_categories')
        .select('id, name, category, sub_category, matchable, category_number')
        .eq('active', true)
        .eq('matchable', true)
        .order('category_number', { ascending: true });

      if (error) {
        console.error('Error fetching grouped matchable supplier categories:', error);
        throw error;
      }

      const categories = data as SupplierCategory[];
      
      // Group categories with their subcategories
      const mainCategories = categories.filter(cat => cat.category);
      const grouped = mainCategories.map(mainCat => ({
        mainCategory: mainCat,
        subCategories: categories.filter(cat => 
          cat.sub_category && 
          !cat.category && 
          cat.category_number === mainCat.category_number
        )
      }));

      return grouped;
    },
  });
};
